import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import Filters from "../components/LogsByDay/Filters";
import LogsTable from "../components/LogsByDay/LogsTable";
import Pagination from "../components/LogsByDay/Pagination";
import ErrorBox from "../components/LogsByDay/ErrorBox";
import SlowChart from "../components/LogsSlow/SlowChart";

export default function LogsSlow() {
    const [threshold, setThreshold] = useState(5);
    const [searchKey, setSearchKey] = useState(0);

    const [filters, setFilters] = useState({
        day: "",
        startTime: "",
        endTime: "",
        clientMsgId: "",
    });

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const formatDayForAPI = (d) => d.replaceAll("-", "");

    // Init default
    useEffect(() => {
        const now = new Date();
        const after30min = new Date(now.getTime() - 30 * 60 * 1000);

        const toDateInput = (d) => d.toISOString().slice(0, 10);
        const toTimeInput = (d) => d.toTimeString().slice(0, 5);

        setFilters({
            day: toDateInput(now),
            startTime: toTimeInput(after30min),
            endTime: toTimeInput(now),
            clientMsgId: "",
        });
    }, []);

    const buildPayload = () => {
        setErrorMsg("");
        const { day, startTime, endTime, clientMsgId } = filters;

        if (!day || !startTime || !endTime) {
            setErrorMsg("Vui lòng chọn ngày và khoảng thời gian.");
            return null;
        }

        const start = new Date(`${day}T${startTime}:00`);
        const end = new Date(`${day}T${endTime}:00`);

        if (end <= start) {
            setErrorMsg("Thời gian kết thúc phải lớn hơn thời gian bắt đầu.");
            return null;
        }

        if (end - start > 30 * 60 * 1000) {
            setErrorMsg("Khoảng thời gian tối đa 30 phút.");
            return null;
        }

        return {
            day: formatDayForAPI(day),
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            client_message_id: clientMsgId || null,
            threshold,
            page,
            page_size: pageSize,
        };
    };

    const fetchLogs = async () => {
        const payload = buildPayload();
        if (!payload) return;

        try {
            setLoading(true);
            setLogs([]);
            const res = await axios.post("/logs/slow", payload);
            setLogs(res.data || []);
        } catch (err) {
            setErrorMsg(
                err.response?.data?.detail || "Lỗi khi gọi API /logs/slow"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!filters.day) return;
        fetchLogs();
    }, [page, pageSize, threshold]);

    return (
        <div className="min-h-screen bg-gray-50 px-6 py-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 border-b pb-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Slow Push Monitor
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Theo dõi bản tin có pushTime lớn.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex gap-2">
                    {[5, 10].map((v) => (
                        <button
                            key={v}
                            onClick={() => {
                                setThreshold(v);
                                setPage(1);
                            }}
                            className={`px-4 py-1.5 rounded-full text-sm border transition ${
                                threshold === v
                                    ? "bg-red-600 text-white border-red-600"
                                    : "bg-white border-gray-300 text-gray-700"
                            }`}
                        >
                            &gt; {v}s
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <Filters
                filters={filters}
                setFilters={setFilters}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onSubmit={() => {
                    setPage(1);
                    setSearchKey((k) => k + 1);
                    fetchLogs();
                }}
                loading={loading}
            />

            {/* Chart */}
            <SlowChart filters={filters} searchKey={searchKey} />

            {/* Error */}
            {errorMsg && <ErrorBox message={errorMsg} />}

            {/* Table */}
            <LogsTable logs={logs} loading={loading} />

            {/* Pagination */}
            <Pagination
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                dataLength={logs.length}
                loading={loading}
            />
        </div>
    );
}
